/**
 * Icon mapping for stat modifiers and actions
 */
import useGameStore from '@store/useGameStore';
import { Swords, Shield, Brain, Sparkle, Rabbit, Footprints, Eye, Heart, Zap, Activity } from 'lucide-react';

/**
 * Lucide icon components for stats
 * Usage: const Icon = STAT_ICONS[statName]; return <Icon size={16} />;
 */
export const STAT_ICONS = {
  strength: Swords,
  armor: Shield,
  psi: Brain,
  will: Sparkle,
  agility: Rabbit,
  speed: Rabbit,
  movement: Footprints,
  max_movement: Footprints,
  vision: Eye,
  hp: Heart,
  max_hp: Heart,
  ap: Zap,
  max_ap: Zap,
  default: Activity,
};

/**
 * SVG color constants for effects (can't use CSS vars in SVG attributes)
 */
export const EFFECT_COLORS = {
  buff: '#22c55e',   // Green
  debuff: '#ef4444', // Red
  neutral: '#ffffff' // White
};

/**
 * Get the appropriate icon for an effect source
 * For now returns emoji strings, will be replaced with React components
 * @param {Object} source - The source object with type, id, and name
 * @returns {string} The emoji icon to display
 */
export const getSourceIcon = (source) => {
  // Handle null/undefined source
  if (!source) {
    console.warn('[getSourceIcon] No source provided');
    return '❓';
  }
  
  // Switch based on source type
  switch (source.type) {
    case 'unit': {
      // For units, we'll show a special emoji for now
      // TODO: Replace with actual UnitIcon component
      console.info(`[getSourceIcon] Unit source: ${source.name} (ID: ${source.id})`);
      return '👤';
    }
    
    case 'effect':
      console.warn(`[getSourceIcon] Effect source not implemented yet:`, source);
      return '❓';
    
    case 'ability':
      console.warn(`[getSourceIcon] Ability source not implemented yet:`, source);
      return '❓';
      
    case 'item':
      console.warn(`[getSourceIcon] Item source not implemented yet:`, source);
      return '❓';
      
    case 'environment':
      console.warn(`[getSourceIcon] Environment source not implemented yet:`, source);
      return '❓';

    case 'planned_action': {
      // Use action_id to get the action template directly (not ephemeral planned_action)
      const gameStore = useGameStore.getState();

      // NEW: Use action_id from source (persisted template ID)
      if (source.action_id) {
        const actionTemplate = gameStore.getActionById(source.action_id);
        if (actionTemplate) {
          return actionTemplate.icon || '⚡';
        }
        console.warn(`[getSourceIcon] Action template not found for action_id: ${source.action_id}`, source);
        return '⚡';
      }

      // FALLBACK: Old format (source.id is planned_action id - ephemeral)
      console.warn(`[getSourceIcon] No action_id in source, falling back to planned_action lookup (ephemeral):`, source);
      const plannedAction = gameStore.getHydratedPlannedActionById(source.id);
      if (plannedAction && plannedAction.action) {
        return plannedAction.action.icon || '⚡';
      }
      console.warn(`[getSourceIcon] Could not find action template or planned action:`, source);
      return '⚡';
    }

    case 'unit_effect': {
      // Source from active effect on unit (e.g., PSI Boost from Overfocus, Burn damage)
      // source = {type: 'unit_effect', id: 8, name: 'Overfocus', origin_type: 'unit', origin_id: 5, ...}
      // Effect modifiers show a generic effect icon
      console.debug(`[getSourceIcon] Unit effect source: ${source.name} (ID: ${source.id})`);
      return '✨'; // Generic effect icon
    }

    default:
      throw new Error(`[getSourceIcon] Unknown source type: ${source.type}. Source: ${JSON.stringify(source)}`);
  }
};

// REMOVED: getModifierIcon - not used anywhere

export const getStatIcon = (statName) => {
  const statIconMap = {
    'strength': '⚔️',
    'armor': '🛡️',
    'movement': '👣',
    'max_movement': '👣',
    'agility': '🐇',
    'speed': '🐇',
    'psi': '🧠',
    'will': '✨',
    'vision': '👁️',
    'ap': '⚡',
    'max_ap': '⚡',
    'hp': '❤️',
    'max_hp': '❤️'
  };

  return statIconMap[statName] || '📊';
};

/**
 * SVG path data for Lucide icons used in stat displays
 * These are extracted from lucide-react for inline SVG usage
 */
const STAT_ICON_PATHS = {
  // Swords - strength
  strength: [
    '<polyline points="14.5 17.5 3 6 3 3 6 3 17.5 14.5"/>',
    '<line x1="13" x2="19" y1="19" y2="13"/>',
    '<line x1="16" x2="20" y1="16" y2="20"/>',
    '<line x1="19" x2="21" y1="21" y2="19"/>',
    '<polyline points="14.5 6.5 18 3 21 3 21 6 17.5 9.5"/>',
    '<line x1="5" x2="9" y1="14" y2="18"/>',
    '<line x1="7" x2="4" y1="17" y2="20"/>',
    '<line x1="3" x2="5" y1="19" y2="21"/>'
  ],
  // Shield - armor
  armor: [
    '<path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z"/>'
  ],
  // Brain - psi
  psi: [
    '<path d="M12 18V5"/>',
    '<path d="M15 13a4.17 4.17 0 0 1-3-4 4.17 4.17 0 0 1-3 4"/>',
    '<path d="M17.598 6.5A3 3 0 1 0 12 5a3 3 0 1 0-5.598 1.5"/>',
    '<path d="M17.997 5.125a4 4 0 0 1 2.526 5.77"/>',
    '<path d="M18 18a4 4 0 0 0 2-7.464"/>',
    '<path d="M19.967 17.483A4 4 0 1 1 12 18a4 4 0 1 1-7.967-.517"/>',
    '<path d="M6 18a4 4 0 0 1-2-7.464"/>',
    '<path d="M6.003 5.125a4 4 0 0 0-2.526 5.77"/>'
  ],
  // Sparkle - will
  will: [
    '<path d="M11.017 2.814a1 1 0 0 1 1.966 0l1.051 5.558a2 2 0 0 0 1.594 1.594l5.558 1.051a1 1 0 0 1 0 1.966l-5.558 1.051a2 2 0 0 0-1.594 1.594l-1.051 5.558a1 1 0 0 1-1.966 0l-1.051-5.558a2 2 0 0 0-1.594-1.594l-5.558-1.051a1 1 0 0 1 0-1.966l5.558-1.051a2 2 0 0 0 1.594-1.594z"/>'
  ],
  // Rabbit - agility/speed
  agility: [
    '<path d="M13 16a3 3 0 0 1 2.24 5"/>',
    '<path d="M18 12h.01"/>',
    '<path d="M18 21h-8a4 4 0 0 1-4-4 7 7 0 0 1 7-7h.2L9.6 6.4a1 1 0 1 1 2.8-2.8L15.8 7h.2c3.3 0 6 2.7 6 6v1a2 2 0 0 1-2 2h-1a3 3 0 0 0-3 3"/>',
    '<path d="M20 8.54V4a2 2 0 1 0-4 0v3"/>',
    '<path d="M7.612 12.524a3 3 0 1 0-1.6 4.3"/>'
  ],
  // Footprints - movement
  movement: [
    '<path d="M4 16v-2.38C4 11.5 2.97 10.5 3 8c.03-2.72 1.49-6 4.5-6C9.37 2 10 3.8 10 5.5c0 3.11-2 5.66-2 8.68V16a2 2 0 1 1-4 0Z"/>',
    '<path d="M20 20v-2.38c0-2.12 1.03-3.12 1-5.62-.03-2.72-1.49-6-4.5-6C14.63 6 14 7.8 14 9.5c0 3.11 2 5.66 2 8.68V20a2 2 0 1 0 4 0Z"/>',
    '<path d="M16 17h4"/>',
    '<path d="M4 13h4"/>'
  ],
  // Eye - vision
  vision: [
    '<path d="M2.062 12.348a1 1 0 0 1 0-.696 10.75 10.75 0 0 1 19.876 0 1 1 0 0 1 0 .696 10.75 10.75 0 0 1-19.876 0"/>',
    '<circle cx="12" cy="12" r="3"/>'
  ],
  // Heart - hp
  hp: [
    '<path d="M2 9.5a5.5 5.5 0 0 1 9.591-3.676.56.56 0 0 0 .818 0A5.49 5.49 0 0 1 22 9.5c0 2.29-1.5 4-3 5.5l-5.492 5.313a2 2 0 0 1-3 .019L5 15c-1.5-1.5-3-3.2-3-5.5"/>'
  ],
  // Zap - ap
  ap: [
    '<path d="M4 14a1 1 0 0 1-.78-1.63l9.9-10.2a.5.5 0 0 1 .86.46l-1.92 6.02A1 1 0 0 0 13 10h7a1 1 0 0 1 .78 1.63l-9.9 10.2a.5.5 0 0 1-.86-.46l1.92-6.02A1 1 0 0 0 11 14z"/>'
  ],
  // Activity - default
  default: [
    '<path d="M22 12h-2.48a2 2 0 0 0-1.93 1.46l-2.35 8.36a.25.25 0 0 1-.48 0L9.24 2.18a.25.25 0 0 0-.48 0l-2.35 8.36A2 2 0 0 1 4.49 12H2"/>'
  ]
};

// Aliases for stats with multiple names
STAT_ICON_PATHS.speed = STAT_ICON_PATHS.agility;
STAT_ICON_PATHS.max_movement = STAT_ICON_PATHS.movement;
STAT_ICON_PATHS.max_hp = STAT_ICON_PATHS.hp;
STAT_ICON_PATHS.max_ap = STAT_ICON_PATHS.ap;

// Short aliases for icon tags (used in [icon:str] format)
STAT_ICON_PATHS.str = STAT_ICON_PATHS.strength;
STAT_ICON_PATHS.arm = STAT_ICON_PATHS.armor;
STAT_ICON_PATHS.mov = STAT_ICON_PATHS.movement;
STAT_ICON_PATHS.agi = STAT_ICON_PATHS.agility;
STAT_ICON_PATHS.vis = STAT_ICON_PATHS.vision;

/**
 * Get Lucide icon as inline SVG string for a stat
 * Used in animations and DOM-based rendering where React components can't be used
 *
 * @param {string} statName - The stat name (strength, armor, psi, etc.)
 * @param {number} size - SVG size in pixels (default: 16)
 * @param {string} color - Stroke color (default: 'currentColor')
 * @returns {string} Inline SVG string
 */
export const getStatIconSVG = (statName, size = 16, color = 'currentColor') => {
  const paths = STAT_ICON_PATHS[statName] || STAT_ICON_PATHS.default;
  const pathsHTML = paths.join('');

  return `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" stroke="${color}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide-stat-icon">${pathsHTML}</svg>`;
};

/**
 * Parse icon tags in text and replace with inline SVG icons.
 * Format: [icon:name] where name is a stat name (str, strength, arm, armor, psi, will, etc.)
 *
 * Examples:
 * - "BLOCKED! (2 [icon:str] vs 5 [icon:arm])" → "BLOCKED! (2 <svg>...</svg> vs 5 <svg>...</svg>)"
 * - "Damage: 10 [icon:psi]" → "Damage: 10 <svg>...</svg>"
 *
 * @param {string} text - Text containing [icon:name] tags
 * @param {number} size - SVG icon size in pixels (default: 14)
 * @returns {string} Text with icon tags replaced by inline SVG
 */
export const parseIconTags = (text, size = 14) => {
  if (!text || typeof text !== 'string') return text;

  // Match [icon:name] pattern
  return text.replace(/\[icon:(\w+)\]/g, (match, iconName) => {
    const paths = STAT_ICON_PATHS[iconName.toLowerCase()];
    if (!paths) {
      console.warn(`[parseIconTags] Unknown icon: ${iconName}`);
      return match; // Return original if unknown
    }
    const pathsHTML = paths.join('');
    return `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide-stat-icon">${pathsHTML}</svg>`;
  });
};

/**
 * Get Lucide icon name for a stat (string version for dynamic imports)
 * @param {string} statName - The stat name
 * @returns {string} Lucide icon component name
 */
export const getStatIconName = (statName) => {
  const statIconMap = {
    'strength': 'Swords',
    'armor': 'Shield',
    'movement': 'Footprints',
    'max_movement': 'Footprints',
    'agility': 'Rabbit',
    'speed': 'Rabbit',
    'psi': 'Brain',
    'will': 'Sparkle',
    'vision': 'Eye',
    'ap': 'Zap',
    'max_ap': 'Zap',
    'hp': 'Heart',
    'max_hp': 'Heart'
  };

  return statIconMap[statName] || 'Activity';
};

/**
 * Get appropriate emoji icon for an effect based on its slug
 * @param {string} effectSlug - The effect slug identifier
 * @returns {string} Emoji icon for the effect
 */
export const getEffectIcon = (effectSlug) => {
  if (!effectSlug) return '✨';
  
  // Normalize the slug to lowercase for comparison
  const slug = effectSlug.toLowerCase();
  
  const effectIconMap = {
    // Damage over time
    'poison': '☠️',
    'poison_apply': '☠️',
    'poison_damage': '☠️',
    'poison_cleanse': '✨',
    'acid': '🧪',
    'burn': '🔥',
    'bleed': '🩸',
    
    // Mental/Psi effects
    'mind_drain': '🧿',
    'mind_drain_apply': '🧿',
    'mind_drain_pulse': '🧿',
    'mind_drain_expire': '✨',
    'mind_control': '🧠',
    'confuse': '😵',
    'fear': '😨',
    'taunt': '😠',

    // Defensive
    'shield': '🛡️',
    'shield_up': '🛡️',
    'defense_aura': '🛡️',
    'fortify': '🛡️',
    'armor_break': '💔',

    // Counter attacks
    'sword-counter-attack': '⚔️',
    'revenge': '💢',

    // Passive abilities
    'snipers-focus': '🎯',
    
    // Energy/AP
    'energy_drain': '⚡',
    'defense_boost': '🛡️',
    'attack_boost': '⚔️',
    'speed_boost': '💨',
    'slow': '🐌',
    
    // Status effects
    'stun': '⭐',
    'stunned': '⚡',
    'freeze': '❄️',
    'blind': '👁️',
    'silence': '🤐',
    
    // Healing/Regen
    'heal': '❤️',
    'regeneration': '💚',
    
    // Death effects
    'death': '💀',
    'unit_death': '💀',
    'unit_poisoned_death': '☠️',
    'psichic_death': '🧿',
    'burn_death': '🔥',
    
    // Buff/Debuff generic
    'buffed': '⬆️',
    'debuffed': '⬇️',
    'shielded': '🛡️',
    
    // Default fallback
    'default': '✨'
  };
  
  // Check for exact match first
  if (effectIconMap[slug]) {
    return effectIconMap[slug];
  }
  
  // Check for partial matches (e.g., 'poison_tick' matches 'poison')
  for (const [key, icon] of Object.entries(effectIconMap)) {
    if (slug.includes(key)) {
      return icon;
    }
  }
  
  // Default sparkle for unknown effects
  return '✨';
};

/**
 * Get fallback icon for non-damage actions based on slug
 * @param {string} actionSlug - The action slug identifier
 * @returns {string} Lucide icon component name
 */
export const getActionFallbackIcon = (actionSlug) => {
  if (!actionSlug) return 'HelpCircle';

  const slug = actionSlug.toLowerCase();

  // Movement actions
  if (slug.includes('move')) return 'Footprints';

  // Defensive actions
  if (slug.includes('defend') || slug.includes('block') || slug.includes('parry')) return 'Shield';

  // Healing actions
  if (slug.includes('heal')) return 'Heart';

  // Observation actions
  if (slug.includes('observe') || slug.includes('scout')) return 'Eye';

  // Default
  return 'HelpCircle';
};

/**
 * Extract damage/healing icon and amount from action properties
 * @param {Object} actionProperties - The action.properties object
 * @param {string} actionSlug - The action slug (for fallback icon)
 * @returns {{icon: string, damage: number|null, damageType: string|null, isHealing: boolean}} Icon name and damage/healing amount
 */
export const getDamageIconFromProperties = (actionProperties, actionSlug) => {
  // Check if action has stat_changes in on_apply trigger
  const onApply = actionProperties?.on_apply;
  const statChanges = onApply?.stat_changes;

  if (statChanges && statChanges.length > 0) {
    // Find the first stat_change that targets HP (damage = decrease, healing = increase)
    const hpDamage = statChanges.find(change =>
      change.target_stat === 'hp' &&
      change.operation_direction === 'decrease'
    );
    const hpHealing = statChanges.find(change =>
      change.target_stat === 'hp' &&
      change.operation_direction === 'increase'
    );

    const hpChange = hpDamage || hpHealing;
    const isHealing = !hpDamage && !!hpHealing;

    if (hpChange) {
      // Extract base value from operations array
      const baseOperation = hpChange.operations?.find(op => op.type === 'base');
      const baseValue = baseOperation?.value || 0;

      // Get damage type (e.g., 'strength', 'psi') - for healing, use Heart icon
      const damageType = hpChange.damage_type;

      if (isHealing) {
        return {
          icon: 'Heart',
          damage: baseValue,
          damageType: null,
          isHealing: true
        };
      }

      if (damageType) {
        return {
          icon: getStatIconName(damageType),
          damage: baseValue,
          damageType: damageType,
          isHealing: false
        };
      }
    }
  }

  // No damage/healing - return fallback icon based on action slug
  return {
    icon: getActionFallbackIcon(actionSlug),
    damage: null,
    damageType: null,
    isHealing: false
  };
};

/**
 * Get action type label for non-damage actions
 * @param {string} actionSlug - The action slug identifier
 * @returns {{icon: string, label: string}} Icon name and type label
 */
export const getActionTypeInfo = (actionSlug) => {
  if (!actionSlug) return { icon: 'HelpCircle', label: 'Action' };

  const slug = actionSlug.toLowerCase();

  // Movement actions
  if (slug.includes('move')) return { icon: 'Footprints', label: 'Movement' };

  // Defensive/Buff actions
  if (slug.includes('defend') || slug.includes('block') || slug.includes('fortify')) {
    return { icon: 'Shield', label: 'Defense' };
  }
  if (slug.includes('boost') || slug.includes('amplify') || slug.includes('buff')) {
    return { icon: 'TrendingUp', label: 'Buff' };
  }

  // Healing actions
  if (slug.includes('heal')) return { icon: 'Heart', label: 'Heal' };

  // Observation actions
  if (slug.includes('observe') || slug.includes('scout')) return { icon: 'Eye', label: 'Scout' };

  // Default
  return { icon: 'Zap', label: 'Action' };
};

/**
 * Format range display string from min/max range values
 * @param {number} minRange - Minimum cast range
 * @param {number} maxRange - Maximum cast range
 * @returns {string} Formatted range string
 */
export const getRangeDisplay = (minRange, maxRange) => {
  // Handle undefined/null
  if (minRange === undefined || minRange === null) minRange = 0;
  if (maxRange === undefined || maxRange === null) maxRange = 0;

  // Self-target (both 0)
  if (minRange === 0 && maxRange === 0) return 'Self';

  // Same min and max
  if (minRange === maxRange) return `${maxRange}`;

  // Range with min 0 (can self-target or adjacent)
  if (minRange === 0) return `0-${maxRange}`;

  // Different min and max
  return `${minRange}-${maxRange}`;
};

/**
 * Determine if AOE is beneficial based on team filters
 * @param {Object} filters - The targeting filters
 * @returns {boolean} True if AOE affects allies/own, false if affects enemies
 */
const isAoeBeneficial = (filters) => {
  if (!filters?.team || !Array.isArray(filters.team)) {
    return false; // Default to harmful if no filters
  }
  const teams = filters.team;
  // Beneficial if targets own/ally and NOT enemies
  const targetsAllies = teams.includes('own') || teams.includes('ally');
  const targetsEnemies = teams.includes('enemy');
  return targetsAllies && !targetsEnemies;
};

/**
 * Extract AOE info from action properties
 * @param {Object} action - The action object
 * @returns {{isAoe: boolean, aoeRange: string|null, aoeData: Object|null, isBeneficial: boolean}} AOE info with full data for preview
 */
export const getAoeInfo = (action) => {
  const emptyResult = {
    isAoe: false,
    aoeRange: null,
    aoeData: null,
    isBeneficial: false
  };

  if (!action?.properties?.on_apply) {
    return emptyResult;
  }

  const onApply = action.properties.on_apply;

  // Helper to build full AOE data object
  const buildAoeData = (targeting) => ({
    shape: targeting.shape || 'square',
    impactMinRange: targeting.impact_min_range ?? 0,
    impactMaxRange: targeting.impact_max_range ?? 0,
    origin: targeting.origin || 'target',
    filters: targeting.filters || {}
  });

  // Check stat_changes for AOE targeting
  const statChanges = onApply.stat_changes || [];
  for (const change of statChanges) {
    if (change.targeting?.type === 'aoe') {
      const targeting = change.targeting;
      const minRange = targeting.impact_min_range ?? 0;
      const maxRange = targeting.impact_max_range ?? 0;
      return {
        isAoe: true,
        aoeRange: getRangeDisplay(minRange, maxRange),
        aoeData: buildAoeData(targeting),
        isBeneficial: isAoeBeneficial(targeting.filters)
      };
    }
  }

  // Check apply_effects for AOE targeting
  const applyEffects = onApply.apply_effects || [];
  for (const effect of applyEffects) {
    if (effect.targeting?.type === 'aoe') {
      const targeting = effect.targeting;
      const minRange = targeting.impact_min_range ?? 0;
      const maxRange = targeting.impact_max_range ?? 0;
      return {
        isAoe: true,
        aoeRange: getRangeDisplay(minRange, maxRange),
        aoeData: buildAoeData(targeting),
        isBeneficial: isAoeBeneficial(targeting.filters)
      };
    }
  }

  return emptyResult;
};

/**
 * Get stat change info for non-HP stat modifications (buffs/debuffs)
 * @param {Object} actionProperties - The action.properties object
 * @returns {{icon: string, isBuff: boolean, statName: string} | null}
 */
const getStatChangeInfo = (actionProperties) => {
  const onApply = actionProperties?.on_apply;

  // Map stat name to icon
  const statIconMap = {
    'strength': 'Swords',
    'armor': 'Shield',
    'psi': 'Brain',
    'will': 'Sparkle',
    'agility': 'Rabbit',
    'max_movement': 'Footprints',
    'movement': 'Footprints',
    'max_ap': 'Zap',
    'ap': 'Zap',
    'vision': 'Eye'
  };

  // Check for direct stat_changes
  const statChanges = onApply?.stat_changes;
  if (statChanges && statChanges.length > 0) {
    const nonHpChange = statChanges.find(change => change.target_stat !== 'hp');
    if (nonHpChange) {
      const statName = nonHpChange.target_stat;
      const isBuff = nonHpChange.operation_direction === 'increase';
      return {
        icon: statIconMap[statName] || 'TrendingUp',
        isBuff,
        statName
      };
    }
  }

  // Check for apply_effects (buffs/debuffs via effects)
  const applyEffects = onApply?.apply_effects;
  if (applyEffects && applyEffects.length > 0) {
    const firstEffect = applyEffects[0];
    const effectSlug = firstEffect?.effect_slug;

    // Try to get team filters from AOE targeting
    const filters = firstEffect?.targeting?.filters;
    const teamFilters = filters?.team || [];

    // Determine if buff or debuff based on team targeting
    let targetsAllies = teamFilters.includes('ally') || teamFilters.includes('own');
    let targetsEnemies = teamFilters.includes('enemy');

    // Fallback: use can_target_* from on_apply for simple targeting
    if (teamFilters.length === 0) {
      targetsAllies = onApply?.can_target_ally || onApply?.can_target_self;
      targetsEnemies = onApply?.can_target_enemy;
    }

    if (targetsAllies && !targetsEnemies) {
      return { icon: 'TrendingUp', isBuff: true, statName: 'buff', effectSlug };
    }
    if (targetsEnemies && !targetsAllies) {
      return { icon: 'TrendingDown', isBuff: false, statName: 'debuff', effectSlug };
    }
    // Mixed targeting - default to buff icon
    if (targetsAllies || targetsEnemies) {
      return { icon: 'Activity', isBuff: true, statName: 'effect', effectSlug };
    }
  }

  return null;
};

/**
 * Extract complete action info for display (damage, type, range)
 * @param {Object} action - The action object
 * @returns {{icon: string, damage: number|null, typeLabel: string, rangeDisplay: string, hasDamage: boolean, isHealing: boolean, statChangeInfo: Object|null}}
 */
export const getActionDisplayInfo = (action) => {
  if (!action) {
    throw new Error('[getActionDisplayInfo] Action is required');
  }

  const onApply = action.properties?.on_apply;

  // Extract range
  const minRange = onApply?.cast_min_range ?? 0;
  const maxRange = onApply?.cast_max_range ?? 0;
  const rangeDisplay = getRangeDisplay(minRange, maxRange);

  // Check for damage or healing
  const damageInfo = getDamageIconFromProperties(action.properties, action.slug);

  if (damageInfo.damage !== null) {
    // Has damage or healing - use appropriate icon
    return {
      icon: damageInfo.icon,
      damage: damageInfo.damage,
      damageType: damageInfo.damageType,
      typeLabel: null,
      rangeDisplay,
      hasDamage: true,
      isHealing: damageInfo.isHealing,
      statChangeInfo: null
    };
  }

  // Check for non-HP stat changes (buffs/debuffs)
  const statChangeInfo = getStatChangeInfo(action.properties);
  if (statChangeInfo) {
    return {
      icon: statChangeInfo.icon,
      damage: null,
      damageType: null,
      typeLabel: statChangeInfo.isBuff ? '+' : '-',
      rangeDisplay,
      hasDamage: false,
      isHealing: false,
      statChangeInfo
    };
  }

  // No damage/healing/stat change - use action type info
  const typeInfo = getActionTypeInfo(action.slug);
  return {
    icon: typeInfo.icon,
    damage: null,
    damageType: null,
    typeLabel: typeInfo.label,
    rangeDisplay,
    hasDamage: false,
    isHealing: false,
    statChangeInfo: null
  };
};

/**
 * Get stat value from unit for formula calculations
 * @param {Object} unit - The unit to get stat from
 * @param {string} statPath - Stat path like 'source.effective_strength'
 * @param {Function} getEffectiveStat - Function to get effective stat value
 * @param {number|null} multiplier - Optional multiplier
 * @returns {number} Stat value (with multiplier applied if present)
 */
const getStatValueForFormula = (unit, statPath, getEffectiveStat, multiplier = null) => {
  if (!unit || !getEffectiveStat) {
    return 0;
  }

  // Extract stat name from path like 'source.effective_strength'
  const pathParts = statPath.split('.');
  const fullStatName = pathParts[pathParts.length - 1];

  // Remove 'effective_' prefix to get base stat name
  const statName = fullStatName.replace('effective_', '');

  const statValue = getEffectiveStat(unit, statName);
  if (statValue && typeof statValue === 'number') {
    // Apply multiplier if present (same as backend: Math.trunc for integer conversion)
    return multiplier ? Math.trunc(statValue * multiplier) : statValue;
  }

  return 0;
};

/**
 * Extract scaling stat name for display
 * @param {string} statPath - Stat path like 'source.effective_psi'
 * @returns {string|null} Scaling stat name or null
 */
const extractScalingStat = (statPath) => {
  const pathParts = statPath.split('.');
  const statName = pathParts[pathParts.length - 1].replace('effective_', '');

  if (statName.includes('psi') || statName === 'psi') return 'psi';
  if (statName.includes('strength') || statName === 'strength') return 'strength';
  if (statName.includes('will') || statName === 'will') return 'will';
  if (statName.includes('agility') || statName === 'agility') return 'agility';
  if (statName.includes('movement') || statName === 'max_movement') return 'movement';

  return null;
};

/**
 * Calculate total action damage including stat bonuses
 * Mirrors backend FormulaCalculator logic for accurate preview
 *
 * @param {Object} action - The action object with properties
 * @param {Object} unit - The unit performing the action (for stat bonuses)
 * @param {Function} getEffectiveStat - Function to get effective stat value
 * @returns {{baseDamage: number, bonus: number, total: number, scalingStat: string|null, isHealing: boolean} | null}
 */
export const calculateActionDamage = (action, unit, getEffectiveStat) => {
  if (!action?.properties?.on_apply?.stat_changes) {
    return null;
  }

  const statChanges = action.properties.on_apply.stat_changes;

  // Find HP damage entry (decrease) or healing entry (increase)
  const hpDamage = statChanges.find(
    change => change.target_stat === 'hp' && change.operation_direction === 'decrease'
  );
  const hpHealing = statChanges.find(
    change => change.target_stat === 'hp' && change.operation_direction === 'increase'
  );

  const hpChange = hpDamage || hpHealing;
  const isHealing = !hpDamage && !!hpHealing;

  if (!hpChange?.operations) {
    return null;
  }

  // Calculate using same logic as backend FormulaCalculator
  let result = 0;
  let baseDamage = 0;
  let scalingStat = null;

  for (const op of hpChange.operations) {
    switch (op.type) {
      case 'base':
        // Base value - starting point
        result = op.value || 0;
        baseDamage = result;
        break;

      case 'add':
        // Add stat value (with optional multiplier)
        if (op.stat) {
          const statValue = getStatValueForFormula(unit, op.stat, getEffectiveStat, op.multiplier);
          result += statValue;

          // Track first scaling stat for display
          if (!scalingStat) {
            scalingStat = extractScalingStat(op.stat);
          }
        } else if (op.value !== undefined) {
          // Add fixed value
          result += op.value;
        }
        break;

      case 'subtract':
        // Subtract stat value (with optional multiplier)
        if (op.stat) {
          const statValue = getStatValueForFormula(unit, op.stat, getEffectiveStat, op.multiplier);
          result -= statValue;
        } else if (op.value !== undefined) {
          // Subtract fixed value
          result -= op.value;
        }
        break;

      case 'multiply':
        // Multiply result by value
        if (op.value !== undefined) {
          result = Math.trunc(result * op.value);
        }
        break;

      case 'divide':
        // Divide result by value
        if (op.value !== undefined && op.value !== 0) {
          result = Math.trunc(result / op.value);
        }
        break;

      case 'min':
        // Ensure result is at least this value (floor)
        if (op.value !== undefined) {
          result = Math.max(result, op.value);
        }
        break;

      case 'max':
        // Ensure result is at most this value (ceiling)
        if (op.value !== undefined) {
          result = Math.min(result, op.value);
        }
        break;

      case 'negate':
        // Flip sign
        result = -result;
        break;

      case 'add_count_units':
        // Requires game context - not available in frontend preview
        // Skip silently as this is a known limitation
        console.debug('[calculateActionDamage] add_count_units not supported in preview');
        break;

      case 'variable_or_default':
        // Requires UnitEffect variables - not available in simple preview
        // Use default value if provided
        if (op.default !== undefined) {
          result += op.default;
        }
        console.debug('[calculateActionDamage] variable_or_default using default value');
        break;

      default:
        throw new Error(
          `[calculateActionDamage] Unknown operation type: "${op.type}". ` +
          `Action: ${action.slug}, Operation: ${JSON.stringify(op)}`
        );
    }
  }

  return {
    baseDamage,
    bonus: result - baseDamage,
    total: result,
    scalingStat,
    isHealing
  };
};